import React, { useState, useEffect, useCallback } from 'react';
import userService from '../services/userService';
import './Feeds.css';

const AdminUserManagement = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ search: '', status: '', role: '' });
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create'); // create, edit, password
    const [selectedUser, setSelectedUser] = useState(null);
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        firstName: '',
        lastName: '',
        email: '',
        contactNumber: '',
        role: 'operator'
    });
    const [formErrors, setFormErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    const fetchUsers = useCallback(async () => {
        try {
            setLoading(true);
            const result = await userService.getUsers(filters);
            if (result.success) {
                setUsers(result.users);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const openCreateModal = () => {
        setModalMode('create');
        setSelectedUser(null);
        setFormData({
            username: '',
            password: '',
            firstName: '',
            lastName: '',
            email: '',
            contactNumber: '',
            role: 'operator'
        });
        setFormErrors({});
        setShowModal(true);
    };

    const openEditModal = (user) => {
        setModalMode('edit');
        setSelectedUser(user);
        setFormData({
            username: user.username,
            password: '',
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email,
            contactNumber: user.contactNumber,
            role: user.role
        });
        setFormErrors({});
        setShowModal(true);
    };

    const openPasswordModal = (user) => {
        setModalMode('password');
        setSelectedUser(user);
        setFormData({ ...formData, password: '' });
        setFormErrors({});
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedUser(null);
        setFormErrors({});
    };

    const validateForm = () => {
        const errors = {};

        if (modalMode === 'create') {
            if (!formData.username.trim()) errors.username = 'Username is required';
            if (!formData.password || formData.password.length < 6) errors.password = 'Password must be at least 6 characters';
        }

        if (modalMode !== 'password') {
            if (!formData.firstName.trim()) errors.firstName = 'First name is required';
            if (!formData.lastName.trim()) errors.lastName = 'Last name is required';
            if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Valid email is required';
            if (!formData.contactNumber.trim()) errors.contactNumber = 'Contact number is required';
        }

        if (modalMode === 'password') {
            if (!formData.password || formData.password.length < 6) errors.password = 'Password must be at least 6 characters';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        try {
            setSubmitting(true);
            let result;

            if (modalMode === 'create') {
                result = await userService.createUser(formData);
            } else if (modalMode === 'edit') {
                const { password, ...updateData } = formData;
                result = await userService.updateUser(selectedUser._id, updateData);
            } else if (modalMode === 'password') {
                result = await userService.resetPassword(selectedUser._id, formData.password);
            }

            if (result.success) {
                setMessage({ type: 'success', text: result.message || 'Operation successful' });
                closeModal();
                fetchUsers();
            }
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.message || 'Operation failed' });
        } finally {
            setSubmitting(false);
        }
    };

    const toggleUserStatus = async (user) => {
        try {
            const newStatus = user.status === 'active' ? 'inactive' : 'active';
            const result = await userService.toggleUserStatus(user._id, newStatus);
            if (result.success) {
                fetchUsers();
                setMessage({ type: 'success', text: result.message });
            }
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.message || 'Failed to update status' });
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString();
    };

    return (
        <div className="feeds-page">
            {/* Message */}
            {message.text && (
                <div className={`message-toast ${message.type}`}>
                    {message.type === 'success' ? '✅' : '⚠️'} {message.text}
                    <button onClick={() => setMessage({ type: '', text: '' })}>✕</button>
                </div>
            )}

            {/* Filters & Actions */}
            <div className="filters-section">
                <div className="filters-grid">
                    <div className="filter-group">
                        <label>Search</label>
                        <input
                            type="text"
                            value={filters.search}
                            onChange={(e) => handleFilterChange('search', e.target.value)}
                            placeholder="Search users..."
                        />
                    </div>

                    <div className="filter-group">
                        <label>Role</label>
                        <select
                            value={filters.role}
                            onChange={(e) => handleFilterChange('role', e.target.value)}
                        >
                            <option value="">All Roles</option>
                            <option value="operator">Operator</option>
                            <option value="packer">Packer</option>
                            <option value="rto">RTO</option>
                            <option value="admin">Admin</option>
                        </select>
                    </div>

                    <div className="filter-group">
                        <label>Status</label>
                        <select
                            value={filters.status}
                            onChange={(e) => handleFilterChange('status', e.target.value)}
                        >
                            <option value="">All Status</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                        </select>
                    </div>

                    <div className="filter-group filter-actions">
                        <button className="btn btn-primary" onClick={openCreateModal}>
                            <i className="fas fa-plus" style={{ marginRight: '8px' }}></i> Add User
                        </button>
                    </div>
                </div>
            </div>

            {/* Users Table */}
            <div className="table-section">
                {loading ? (
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Loading users...</p>
                    </div>
                ) : users.length === 0 ? (
                    <div className="empty-state">
                        <i className="fas fa-users-slash" style={{ fontSize: '48px', opacity: 0.2, marginBottom: '16px' }}></i>
                        <p>No users found</p>
                    </div>
                ) : (
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Username</th>
                                <th>Full Name</th>
                                <th>Email</th>
                                <th>Contact</th>
                                <th>Role</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map(user => (
                                <tr key={user._id}>
                                    <td className="awb-cell">{user.username}</td>
                                    <td>{user.firstName} {user.lastName}</td>
                                    <td>{user.email}</td>
                                    <td>{user.contactNumber}</td>
                                    <td>
                                        <span className={`role-badge ${user.role}`}>
                                            {user.role}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`status-badge ${user.status}`}>
                                            {user.status}
                                        </span>
                                    </td>
                                    <td className="date-cell">{formatDate(user.createdAt)}</td>
                                    <td>
                                        <div className="action-btns">
                                            <button
                                                className="btn btn-sm btn-secondary"
                                                onClick={() => openEditModal(user)}
                                                title="Edit User"
                                            >
                                                <i className="fas fa-edit"></i>
                                            </button>
                                            <button
                                                className="btn btn-sm btn-warning"
                                                onClick={() => openPasswordModal(user)}
                                                title="Reset Password"
                                            >
                                                <i className="fas fa-key"></i>
                                            </button>
                                            <button
                                                className={`btn btn-sm ${user.status === 'active' ? 'btn-danger' : 'btn-success'}`}
                                                onClick={() => toggleUserStatus(user)}
                                                title={user.status === 'active' ? 'Deactivate' : 'Activate'}
                                            >
                                                <i className={`fas ${user.status === 'active' ? 'fa-user-slash' : 'fa-user-check'}`}></i>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* User Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>
                                {modalMode === 'create' && 'Create User'}
                                {modalMode === 'edit' && 'Edit User'}
                                {modalMode === 'password' && 'Reset Password'}
                            </h2>
                            <button className="close-btn" onClick={closeModal}>✕</button>
                        </div>
                        <div className="modal-body">
                            {modalMode !== 'password' && (
                                <>
                                    {modalMode === 'create' && (
                                        <div className="form-group">
                                            <label>Username</label>
                                            <input
                                                type="text"
                                                value={formData.username}
                                                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                                                placeholder="Enter username"
                                            />
                                            {formErrors.username && <span className="form-error">{formErrors.username}</span>}
                                        </div>
                                    )}

                                    {modalMode === 'create' && (
                                        <div className="form-group">
                                            <label>Password</label>
                                            <input
                                                type="password"
                                                value={formData.password}
                                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                                placeholder="Enter password"
                                            />
                                            {formErrors.password && <span className="form-error">{formErrors.password}</span>}
                                        </div>
                                    )}

                                    <div className="form-row">
                                        <div className="form-group">
                                            <label>First Name</label>
                                            <input
                                                type="text"
                                                value={formData.firstName}
                                                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                                                placeholder="First name"
                                            />
                                            {formErrors.firstName && <span className="form-error">{formErrors.firstName}</span>}
                                        </div>
                                        <div className="form-group">
                                            <label>Last Name</label>
                                            <input
                                                type="text"
                                                value={formData.lastName}
                                                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                                                placeholder="Last name"
                                            />
                                            {formErrors.lastName && <span className="form-error">{formErrors.lastName}</span>}
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label>Email</label>
                                        <input
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            placeholder="Enter email"
                                        />
                                        {formErrors.email && <span className="form-error">{formErrors.email}</span>}
                                    </div>

                                    <div className="form-group">
                                        <label>Contact Number</label>
                                        <input
                                            type="text"
                                            value={formData.contactNumber}
                                            onChange={(e) => setFormData({ ...formData, contactNumber: e.target.value })}
                                            placeholder="Enter contact number"
                                        />
                                        {formErrors.contactNumber && <span className="form-error">{formErrors.contactNumber}</span>}
                                    </div>

                                    <div className="form-group">
                                        <label>Role</label>
                                        <select
                                            value={formData.role}
                                            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                        >
                                            <option value="operator">Operator</option>
                                            <option value="packer">Packer</option>
                                            <option value="rto">RTO</option>
                                            <option value="admin">Admin</option>
                                        </select>
                                    </div>
                                </>
                            )}

                            {modalMode === 'password' && (
                                <div className="form-group">
                                    <label>New Password for {selectedUser?.username}</label>
                                    <input
                                        type="password"
                                        value={formData.password}
                                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                        placeholder="Enter new password"
                                    />
                                    {formErrors.password && <span className="form-error">{formErrors.password}</span>}
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary" onClick={closeModal}>
                                Cancel
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handleSubmit}
                                disabled={submitting}
                            >
                                {submitting ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminUserManagement;
